import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { seoCycle, seoAdvanceCycle } from "@/lib/seo/seo-os.functions";
import { SeoPage, Stat, Panel, Pill, Loading, Empty, DataTable, num } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/seo/cycle")({
  head: () => ({ meta: [{ title: "SEO cycle · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: CyclePage,
});

const PHASE: Record<string, string> = {
  diagnose: "Week 1 — find the problems",
  execute: "Week 2 — make the changes",
  evaluate: "Week 3 — measure the results",
};

function CyclePage() {
  const load = useServerFn(seoCycle);
  const advance = useServerFn(seoAdvanceCycle);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["seo-cycle"], queryFn: () => load() });
  const d = q.data as any;

  const next = useMutation({
    mutationFn: () => advance({}),
    onSuccess: (r: any) => {
      toast.success(r.action === "started" ? "New three-week cycle started." : "Cycle updated.");
      qc.invalidateQueries({ queryKey: ["seo-cycle"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not advance the cycle."),
  });

  const report = d?.report;

  return (
    <SeoPage
      title="Three-week cycle"
      description="Work runs in three-week cycles: a week to diagnose, a week to make changes, a week to measure. Progress is judged over the whole cycle, not day to day."
      actions={
        <Button onClick={() => next.mutate()} disabled={next.isPending}>
          {next.isPending ? "Working…" : d?.cycle ? "Advance cycle" : "Start a cycle"}
        </Button>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : !d?.cycle ? (
        <Empty>No cycle running. Start one to set goals and track them over three weeks.</Empty>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            <Stat label="Phase" value={PHASE[d.phase ?? ""] ?? d.phase} />
            <Stat label="Day" value={`${(d.dayIndex ?? 0) + 1} of 21`} />
            <Stat label="Days remaining" value={num(d.daysRemaining ?? 0)} />
            <Stat label="Status" value={d.cycle.status} />
          </div>

          <Panel title="This cycle's goals">
            {(d.cycle.goals ?? []).length === 0 ? (
              <Empty>No goals recorded.</Empty>
            ) : (
              <ul className="list-disc space-y-1 pl-5 text-sm">
                {(d.cycle.goals as string[]).map((g) => (
                  <li key={g}>{g}</li>
                ))}
              </ul>
            )}
          </Panel>

          {report ? (
            <Panel title="Current numbers" subtitle={`${report.period.start} → ${report.period.end}`}>
              <div className="grid gap-3 sm:grid-cols-4">
                <Stat label="Clicks" value={num(report.search.current.clicks)} sub={`${report.search.delta.clicks >= 0 ? "+" : ""}${report.search.delta.clicks} vs prior week`} />
                <Stat label="Impressions" value={num(report.search.current.impressions)} sub={`${report.search.delta.impressions >= 0 ? "+" : ""}${report.search.delta.impressions} vs prior week`} />
                <Stat label="Actions awaiting approval" value={num(report.actions.awaitingApproval)} />
                <Stat label="Enquiries this week" value={num(report.conversions.leadsThisWeek ?? 0)} />
              </div>
            </Panel>
          ) : null}

          <Panel title="Previous cycles">
            {(d.history ?? []).length <= 1 ? (
              <Empty>This is your first cycle.</Empty>
            ) : (
              <DataTable
                head={["Start", "End", "Phase", "Status"]}
                rows={(d.history as any[]).map((c) => [
                  c.cycle_start,
                  c.cycle_end,
                  <Pill key={c.id} tone={c.status === "complete" ? "good" : "neutral"}>
                    {c.phase}
                  </Pill>,
                  c.status,
                ])}
              />
            )}
          </Panel>
        </>
      )}
    </SeoPage>
  );
}
